import type { InitiativeOptions } from "../types";

export type LoginMode = "sso" | "manual" | null;

/**
 * Recupera un query param usato per mostrare solo una tipolgia di login
 * se loginType è "sso" allora viene mostrato solo il login SSO
 * se loginType è "manual" allora viene mostrato solo il login manuale
 */
export const getLoginType = (loginMode: LoginMode) => {
  const urlParams = new URLSearchParams(window.location.search);
  let loginType = loginMode?.toLowerCase();
  const urlLoginType = urlParams.get("loginType");
  loginType = (urlLoginType === "sso" || urlLoginType === "manual") ? urlLoginType : (loginType || "");

  const identityProvider = urlParams.get("identity_provider");
  // se presente identity_provider allora loginType è sso
  if (identityProvider) {
    loginType = "sso";
  }

  return loginType;
};

/**
 * Verifica se un URL ha un path non valido
 * @param fromUrl - L'URL da verificare
 * @returns true se il path è nella lista dei path non validi
 */
export const hasInvalidPath = (fromUrl: string | null) => {
  if (!fromUrl) {
    return false;
  }

  try {
    const url = new URL(fromUrl);
    const pathname = url.pathname;

    // Lista dei path non validi (che iniziano con questi prefissi)
    const invalidPathPrefixes = [
      "refresh",
      "logout",
      "not-found",
      "privacy",
      "terms-conditions",
      "cookie",
      "landing",
      "register",
      "agreements",
      "signup",
      "access",
      "maintenance-page"
    ];

    // Verifica che il path non inizi con nessuno dei prefissi non validi
    return invalidPathPrefixes.some(prefix =>
      pathname.startsWith(`/${prefix}`)
    );
  } catch (error) {
    // Se l'URL non è valido, considera il path come non valido
    return true;
  }
};

export const hasValidFromUrl = (fromUrl: string | null, initiatives: InitiativeOptions[]) => {
  console.log("🔍 [DEBUG] hasValidFromUrl chiamata con:", {
    fromUrl,
    initiatives: initiatives?.map(i => ({
      id: i.id,
      label: i.label,
      urlDomain: i.urlDomain,
      urlCorporateDomain: i.urlCorporateDomain,
      fullDomain: `${i.urlDomain}.${i.urlCorporateDomain}`
    })),
    initiativesCount: initiatives?.length || 0
  });

  if (!fromUrl || !Array.isArray(initiatives)) {
    console.log("❌ [DEBUG] hasValidFromUrl ritorna FALSE - fromUrl o initiatives non validi:", {
      fromUrl: !!fromUrl,
      initiativesIsArray: Array.isArray(initiatives),
      initiativesLength: initiatives?.length
    });
    return false;
  }

  try {
    const fromUrlObj = new URL(fromUrl);
    const fromUrlHost = fromUrlObj.host.toLowerCase();
    console.log("🌐 [DEBUG] Parsing fromUrl:", {
      originalFromUrl: fromUrl,
      parsedHost: fromUrlHost,
      pathname: fromUrlObj.pathname
    });

    // Verifica che il dominio sia presente nelle iniziative
    const matchResult = initiatives.some(
      (initiative) => {
        console.log("🔄 [DEBUG] Verificando iniziativa:", {
          initiativeId: initiative.id,
          initiativeLabel: initiative.label,
          urlDomain: initiative.urlDomain,
          urlCorporateDomain: initiative.urlCorporateDomain
        });

        if (!initiative.urlDomain || !initiative.urlCorporateDomain) {
          console.log("⚠️ [DEBUG] Iniziativa skippata - domini mancanti:", {
            initiativeId: initiative.id,
            hasUrlDomain: !!initiative.urlDomain,
            hasUrlCorporateDomain: !!initiative.urlCorporateDomain
          });
          return false;
        }

        const initiativeDomain = `${initiative.urlDomain}.${initiative.urlCorporateDomain}`.toLowerCase();
        console.log("🔗 [DEBUG] Confronto domini:", {
          fromUrlHost,
          initiativeDomain,
          exactMatch: fromUrlHost === initiativeDomain
        });

        // Controlla corrispondenza esatta del dominio
        if (fromUrlHost === initiativeDomain) {
          console.log("✅ [DEBUG] MATCH ESATTO trovato!", {
            initiativeId: initiative.id,
            fromUrlHost,
            initiativeDomain
          });
          return true;
        }

        // Controlla se è un sottodominio della stessa iniziativa
        const corporateDomainLower = initiative.urlCorporateDomain.toLowerCase();
        const isSubdomain = fromUrlHost.endsWith(`${corporateDomainLower}`);
        console.log("🔗 [DEBUG] Verifica sottodominio:", {
          fromUrlHost,
          corporateDomainLower,
          endsWithCorporateDomain: isSubdomain
        });

        if (isSubdomain) {
          console.log("✅ [DEBUG] MATCH SOTTODOMINIO trovato!", {
            initiativeId: initiative.id,
            fromUrlHost,
            corporateDomainLower
          });
          return true;
        }

        console.log("❌ [DEBUG] Nessun match per questa iniziativa:", {
          initiativeId: initiative.id
        });
        return false;
      }
    );

    console.log("🎯 [DEBUG] hasValidFromUrl risultato finale:", {
      fromUrl,
      fromUrlHost,
      matchResult,
      totalInitiatives: initiatives.length
    });

    return matchResult;
  } catch (error) {
    console.error("💥 [DEBUG] hasValidFromUrl errore nel parsing URL:", {
      fromUrl,
      error: error instanceof Error ? error.message : String(error)
    });
    // Se l'URL non è valido, ritorna false
    return false;
  }
};
